export const timelineGroupingWindowMilliseconds = 60 * 1000;
const standaloneTimelineEventTypes = new Set([
    "BRAND_CREATED",
    "SALES_SESSION",
    "SASS_SESSION",
    "LEGACY_ACTIVITY",
    "OFFER_CREATED",
    "OFFER_PAID",
    "OFFER_ACCEPTED"
]);

// Normalizes one actor identity value for reliable event grouping.
const read_timeline_actor_token = (value) => {
    return `${value || ""}`.trim().toLowerCase();
};

// Reads all usable IDs, emails, and names for one event actor.
const read_timeline_actor_tokens = (event) => {
    const actor = event?.by || {};
    const actorTokens = [
        actor?.userId,
        actor?.id,
        actor?._id,
        actor?.email,
        actor?.username,
        actor?.name,
        actor?.fullName,
        actor?.displayName,
        ...(actor?.aliases || [])
    ].map(read_timeline_actor_token).filter(Boolean).filter((value, index, values) => values.indexOf(value) === index);
    const meaningfulActorTokens = actorTokens.filter((value) => value !== "system");
    const tokenReaders = {
        true: () => meaningfulActorTokens,
        false: () => [`system:${read_timeline_actor_token(event?.sourceApp)}`]
    };

    return tokenReaders[Boolean(meaningfulActorTokens.length)]();
};

// Creates one visual block anchored to its newest canonical event.
const read_timeline_event_group = (event) => {
    const correlationId = read_timeline_actor_token(event?.correlationId);

    return {
        groupKey: `timeline-group-${event?.eventId || event?.sourceKey}`,
        changedAt: event?.changedAt,
        actorTokens: read_timeline_actor_tokens(event),
        correlationIds: correlationId ? [correlationId] : [],
        events: [event]
    };
};

// Checks whether an adjacent event belongs to the current actor and minute.
const read_timeline_event_joins_group = (group, event) => {
    const groupTime = new Date(group?.changedAt || 0).getTime();
    const eventTime = new Date(event?.changedAt || 0).getTime();
    const timeDifference = Math.abs(groupTime - eventTime);
    const timeMatches = Number.isFinite(timeDifference) && timeDifference <= timelineGroupingWindowMilliseconds;
    const actorTokens = read_timeline_actor_tokens(event);
    const actorMatches = actorTokens.some((actorToken) => group?.actorTokens?.includes(actorToken));
    const correlationId = read_timeline_actor_token(event?.correlationId);
    const correlationMatches = Boolean(correlationId) && group?.correlationIds?.includes(correlationId);
    const containsStandaloneEvent = standaloneTimelineEventTypes.has(event?.type)
        || group?.events?.some((groupEvent) => standaloneTimelineEventTypes.has(groupEvent?.type));

    return Boolean(group?.events?.length)
        && !containsStandaloneEvent
        && timeMatches
        && (correlationMatches || actorMatches);
};

// Adds an event to the current visual block without changing its anchor.
const read_groups_with_joined_event = (groups, event) => {
    const currentGroup = groups[groups.length - 1];
    const joinedGroup = {
        ...currentGroup,
        actorTokens: [...new Set([
            ...(currentGroup?.actorTokens || []),
            ...read_timeline_actor_tokens(event)
        ])],
        correlationIds: [...new Set([
            ...(currentGroup?.correlationIds || []),
            read_timeline_actor_token(event?.correlationId)
        ].filter(Boolean))],
        events: [...currentGroup.events, event]
    };

    return [...groups.slice(0, -1), joinedGroup];
};

// Starts a new visual block for an event outside the current group contract.
const read_groups_with_new_event = (groups, event) => {
    return [...groups, read_timeline_event_group(event)];
};

const timelineGroupActionLookup = {
    true: read_groups_with_joined_event,
    false: read_groups_with_new_event
};

// Groups adjacent canonical events by actor and a fixed one-minute window.
export const read_timeline_event_groups = (events) => {
    return events.reduce((groups, event) => {
        const currentGroup = groups[groups.length - 1];
        const joinsCurrentGroup = read_timeline_event_joins_group(currentGroup, event);

        return timelineGroupActionLookup[joinsCurrentGroup](groups, event);
    }, []);
};
